// 2026-10-07 (Charteris Phase 2.5, Task 4): parameters widened from
// JapanSignal to GatedFuturesSignal (JapanSignal extends it) so these
// functions satisfy FuturesMoveCard's generic `describe`/`anchorNote`
// callback types when bound from NiyMoveCard.vue. No behavioural change —
// every field these functions read is shared by both types.
import type { GatedFuturesSignal } from '../../api/client'

// Mirrors the reason vocabulary produced by signal_core's
// gge_mt_signal.decide() + japan_mt_signal.compute_niy_m() for the JAPAN
// fund. Kept as a pure lookup table so it can be unit tested without a
// running signal service.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Manual day+month formatter (not fmtShortDate / Intl) so the label is
// locale-stable — Node/ICU renders en-GB 'short' September as "Sept", which
// would break the exact-match anchor-note sentence. Exported so NiyMoveCard
// uses this SAME formatter for its anchor line — otherwise the card could
// show "Sept" in one line and "Sep" in the anchor-note sentence below it in
// the JPX-holiday case. fmtShortDate (fundConfig.ts) stays untouched; other
// funds still use it.
export function fmtDayMon(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${d} ${MONTHS[m - 1]}`
}

/** Signed whole basis points, e.g. +42 / -13. Shared with charterisReasons.ts. */
export function formatSignedBp(m: number): string {
  const bp = Math.round(m * 10000)
  return bp >= 0 ? `+${bp}` : `${bp}`
}

function whyFromMReason(mReason: string | null | undefined): string {
  if (mReason === 'no_bars') return 'no NIY bars today'
  if (mReason === 'no_bar_le_now') return 'no NIY bar before 08:40'
  if (mReason === 'no_anchor') return 'no Tokyo-close anchor'
  if (mReason === 'contract_roll') return 'NIY contract rolled since the anchor'
  const staleMatch = mReason?.match(/^decision_print_stale_(\d+)s$/)
  if (staleMatch) return `last NIY print ${staleMatch[1]}s old`
  return 'NIY move unavailable'
}

/** The shared gge_mt_signal.decide() threshold-reason codes. Shared with charterisReasons.ts. */
export const NUMERIC_REASONS: ReadonlySet<string> = new Set([
  'm_ge_threshold',
  'm_below_threshold',
  'm_above_buy_threshold',
  'm_below_sell_threshold',
  'dead_zone_hold_buy',
  'dead_zone_hold_cash',
])

export function describeJapanReason(s: GatedFuturesSignal): { text: string; tone: 'ok' | 'amber' } {
  const reason = s.reason

  if (reason && NUMERIC_REASONS.has(reason)) {
    const hasNumbers = s.m_0840 != null && s.threshold_bps != null
    if (!hasNumbers) return { text: 'NIY move unavailable', tone: 'ok' }
    const x = formatSignedBp(s.m_0840 as number)
    const t = Math.round(s.threshold_bps as number)
    switch (reason) {
      case 'm_ge_threshold':
        return { text: `NIY ${x} bp since Tokyo close — at or above the ${t} bp threshold`, tone: 'ok' }
      case 'm_below_threshold':
        return { text: `NIY ${x} bp since Tokyo close — below the ${t} bp threshold`, tone: 'ok' }
      case 'm_above_buy_threshold':
        return { text: `NIY ${x} bp — above the buy threshold`, tone: 'ok' }
      case 'm_below_sell_threshold':
        return { text: `NIY ${x} bp — below the sell threshold`, tone: 'ok' }
      case 'dead_zone_hold_buy':
        return { text: `NIY ${x} bp — inside the dead zone, staying invested`, tone: 'ok' }
      default:
        return { text: `NIY ${x} bp — inside the dead zone, staying in cash`, tone: 'ok' }
    }
  }

  if (reason === 'degraded_carry') {
    return { text: `Degraded — ${whyFromMReason(s.m_reason)}; carried the previous position`, tone: 'amber' }
  }
  if (reason === 'degraded_carry_bootstrap') {
    return { text: `Degraded — ${whyFromMReason(s.m_reason)}; no previous position, defaulted to BUY`, tone: 'amber' }
  }

  return { text: reason || 'NIY move unavailable', tone: 'amber' }
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Walks back from todayLondon over Saturday/Sunday only (no holiday
// calendar here — the JPX calendar is what makes anchor_session diverge
// from this, which is exactly what the sentence below flags).
function prevBusinessDayIso(todayIso: string): string {
  const d = new Date(`${todayIso}T00:00:00`)
  d.setDate(d.getDate() - 1)
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() - 1)
  }
  return toIsoDate(d)
}

export function japanAnchorNote(s: GatedFuturesSignal, todayLondon: string): string | null {
  if (s.m_reason === 'contract_roll') {
    return 'NIY contract rolled since the anchor — decision carried'
  }
  if (!s.anchor_session) return null
  const prevBizDay = prevBusinessDayIso(todayLondon)
  if (s.anchor_session < prevBizDay) {
    return `Tokyo was closed on the previous business day — anchored on the ${fmtDayMon(s.anchor_session)} close`
  }
  return null
}
